import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  WorkflowTooltip,
  WorkflowTooltipWrap,
} from "@via/components/tooltip/workflow-tooltip.tsx";
import {
  HandleTypes,
  WorkflowEditTypes,
} from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import { getHandleClass } from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { NODE_TYPES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers";
import { TooltipInput } from "@via/utils/interfaces.tsx";
import { Handle, NodeToolbar, Position, useReactFlow } from "@xyflow/react";
import { type JSX, memo, useMemo } from "react";

const OutputNode = ({
  data,
  id,
  onDeleteOutput,
  onEditOutput,
  onHandleHover,
  onHandleLeave,
  isEditingAllowed,
}: any): JSX.Element => {
  const { getEdges, getNode } = useReactFlow();
  const edges = getEdges();
  const isTargetConnected = useMemo(
    () => edges.some(edge => edge.target === id),
    [edges]
  );
  // Check if a specific handle is connected
  const isHandleConnected = useMemo(
    () => (handle: string) => {
      return edges.some(
        edge => edge.target === id && edge.targetHandle === handle
      );
    },
    [edges, id]
  );
  const isValidSourceConnection = (connection, file): boolean => {
    if (!connection) return false;

    const { source, sourceHandle } = connection;
    const node = getNode(source);

    if (!node || node?.type === NODE_TYPES.InputNode) return false;
    if (!isTargetConnected) {
      return true;
    }
    const outputHandles = node.data.outputHandles as unknown as string[];
    const tooltips = node.data.tooltips as unknown as {
      outputs: TooltipInput[];
    };

    const match = outputHandles.indexOf(sourceHandle as string);
    if (match === -1 || match === undefined) return false;

    return tooltips.outputs[match]?.fileType === file;
  };
  return (
    <div className={`flex ${data.className}`}>
      <NodeToolbar
        className={classNames(
          "flex flex-row rounded-md border-[1px] border-gray-300 bg-white p-2",
          !isEditingAllowed ? "pr-1" : ""
        )}>
        <PencilIcon
          className="w-[20px] cursor-pointer pr-1 hover:text-gray-600"
          onClick={() => onEditOutput(WorkflowEditTypes.EditOutput, data)}
        />
        {isEditingAllowed && (
          <TrashIcon
            className="w-[20px] cursor-pointer pl-1 hover:text-gray-600"
            onClick={() => onDeleteOutput(id)}
          />
        )}
      </NodeToolbar>
      <div title={data.label} className="pt-8 text-[13px]">
        {data.label?.length > 7 ? `${data.label.slice(0, 7)}...` : data.label}
      </div>
      {data?.inputHandles.map((handle, i) => {
        const handleConnected = isHandleConnected(handle);
        return (
          <WorkflowTooltipWrap key={`${i}-${handle}`}>
            <Handle
              id={handle}
              type="target"
              className={getHandleClass(
                data,
                data?.tooltips?.inputs[i]?.fileType,
                handle,
                HandleTypes.Target,
                id,
                handleConnected
              )}
              isValidConnection={connection =>
                isValidSourceConnection(
                  connection,
                  data?.tooltips?.inputs[i]?.fileType
                )
              }
              position={Position.Left}
              isConnectable={!isTargetConnected}
              isConnectableStart={!isTargetConnected}
              onMouseEnter={() =>
                onHandleHover(
                  data?.tooltips?.inputs[i]?.fileType,
                  HandleTypes.Target,
                  handle,
                  id,
                  NODE_TYPES.OutputNode,
                  handleConnected
                )
              }
              onMouseLeave={onHandleLeave}>
              <WorkflowTooltip data={data?.tooltips?.inputs[i]} />
            </Handle>
          </WorkflowTooltipWrap>
        );
      })}
    </div>
  );
};
export default memo(OutputNode);
